import {
  addOrReplaceByKey,
  distinctByKey,
  HttpStatusCode,
} from '@robovision/rvai-utils'
import { AxiosError } from 'axios'
import { defineStore } from 'pinia'
import slugify from 'slugify'
import {
  reactive,
  readonly,
  toRefs,
} from 'vue'
import { Tag } from '~domain/Tag'
import {
  dataApi,
  hubApi,
} from '~services/api'
import { acceptStatus } from '~utils/api'


interface DeleteTags {
  cascadeSamples: boolean
  poolId: string
  projectId: string
  tagId: string
}

export const useTagsStore = defineStore('tags', () => {
  const state = reactive({
    projectTags: new Map<string, Tag[]>(),
    thumbnailUrls: new Map<string, Promise<string | undefined>>(),
  })

  const getters = {
    getTags (
      projectId: string,
      {
        algorithmJobId,
        artifactId,
      }: {
        algorithmJobId?: string
        artifactId?: string
      } = {},
    ): Tag[] {
      return (state.projectTags.get(projectId) ?? []).filter(j => {
        return !!(
          (
            !algorithmJobId || j.attributes.algorithmJobId === algorithmJobId
          ) && (
            // Backwards compatibility: if now artifact ID attribute is set, show it
            !artifactId || !j.attributes.artifactId || j.attributes.artifactId === artifactId
          )
        )
      })
    },
  }

  const actions = {
    async deleteTags ({
      cascadeSamples,
      poolId,
      projectId,
      tagId,
    }: DeleteTags) {
      if (cascadeSamples) {
        await dataApi.pools.deleteSamples(poolId, {
          sampleIds: [],
          tagIds: [[tagId]],
        })
      }
      await dataApi.tags.deleteTag(tagId)

      const remainingTags = state.projectTags.get(projectId)?.filter(t => t.tagId !== tagId) ?? []
      state.projectTags.set(projectId, remainingTags)
    },

    async getThumbnailUrl (projectId: string, tagId: string): Promise<string | undefined> {
      if (!state.thumbnailUrls.has(tagId)) {
        const promise = (async () => {
          try {
            const tag = state.projectTags.get(projectId)?.find(tag => tag.tagId === tagId)
            if (!tag) return undefined
            const { data: { samples } } = await dataApi.pools.listPoolSamples(
              tag.poolId,
              {
                limit: 1,
                tagIds: [[tagId]],
              },
            )
            const [sample] = samples
            return sample && `/datastore${sample.thumbnail}`
          } catch (e) {
            console.error(e)
          }
        })()
        state.thumbnailUrls.set(tagId, promise)
      }
      return state.thumbnailUrls.get(tagId)
    },

    async loadProjectTag (projectId: string, tagId: string, signal?: AbortSignal): Promise<Tag | undefined> {
      try {
        const response = await dataApi.tags.getTagMetadata(tagId, {
          signal,
          validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND),
        })
        if (!response || response.status === HttpStatusCode.NOT_FOUND) return undefined

        const tag = Tag.fromResponse(response.data)
        const existingTags = state.projectTags.get(projectId) ?? []
        const updatedProjectTags = [...existingTags.filter(t => t.tagId !== tag.tagId), tag]

        state.projectTags.set(projectId, updatedProjectTags)
        return tag
      } catch (e) {
        if (e instanceof AxiosError && e.code === 'ERR_CANCELED') {
          // do nothing
        } else {
          throw e
        }
      }
    },

    async loadProjectTags (
      projectId: string,
      {
        loadMetadataForImports,
        metadataLimit,
        unique,
      }: {
        loadMetadataForImports?: boolean
        metadataLimit?: number
        unique?: boolean
      } = {},
    ): Promise<void> {
      const { data: datapools } = await hubApi.datapools.listDatapools({ projectId })
      if (!datapools.items[0]) return

      const [{ id: poolId }] = datapools.items
      let { data: { tags } } = await dataApi.pools.listPoolTags(poolId)

      let updatedTags: Tag[] = []
      if (unique) {
        const existedTagIds = state.projectTags.get(projectId)?.map(t => t.tagId) ?? []
        const removedTagIds = existedTagIds.filter(t => !tags.map(t => t.tagId).includes(t))
        // handle removed tags
        updatedTags = state.projectTags.get(projectId)?.filter(t => !removedTagIds.includes(t.tagId)) ?? []
        // handle added tags
        tags = tags.filter(t => !existedTagIds.includes(t.tagId))
      }

      const importTags = loadMetadataForImports
        ? tags.filter(t =>
          t.attributes?.jobType === 'IMPORT' ||
          t.attributes?.tagType === 'IMPORT' ||
          t.attributes?.tagType === 'RECORDING')
        : tags
      const slicedTags = metadataLimit !== undefined ? importTags.slice(0, metadataLimit) : importTags
      const projectTags = await Promise.all(slicedTags.map(async t => {
        const { data: metadata } = await dataApi.tags.getTagMetadata(t.tagId)
        return Tag.fromResponse(metadata)
      }))

      state.projectTags.set(projectId, distinctByKey([
        ...(unique && metadataLimit === 0 ? tags.map(Tag.fromResponse) : projectTags),
        ...(unique ? updatedTags : tags.map(Tag.fromResponse)),
      ], 'tagId'))
    },

    async removeProjectTag (projectId: string, tag: Tag, cascadeSamples: boolean): Promise<void> {
      const { data: datapools } = await hubApi.datapools.listDatapools({ projectId })
      if (!datapools.items[0]) return

      const [{ id: poolId }] = datapools.items
      await this.deleteTags({
        cascadeSamples,
        poolId,
        projectId,
        tagId: tag.tagId,
      })
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },

    setTags (projectId: string, tags: Tag[]) {
      state.projectTags.set(projectId, tags)
    },

    setTagSampleCount (projectId: string, tagId: string, sampleCount: number) {
      const tag = state.projectTags
        .get(projectId)
        ?.find(t => t.tagId === tagId)
      if (!tag) return
      tag.sampleCount = sampleCount
    },

    async updateTagName (projectId: string, tagId: string, name: string) {
      const { data } = await dataApi.tags.getTagMetadata(tagId)
      await dataApi.tags.updateTagMetadata(tagId, {
        attributes: data.attributes,
        description: data.description,
        name,
        ownerId: data.ownerId, // weird?
        slug: slugify(name),
      })

      state.projectTags.set(projectId, addOrReplaceByKey(
        state.projectTags.get(projectId) ?? [],
        Tag.fromResponse({
          ...data,
          name,
        }),
        'tagId',
      ))
    },
  }

  return {
    ...toRefs(readonly(state)),
    ...getters,
    ...actions,
  }
})
